import {RootState} from '../../rootReducer';
import {getAllWorkdays} from '../workday/selectors';
import {getAllWorkspaceReservations} from '../workspaceReservation/selectors';
import {isWithinInterval, parseISO} from 'date-fns';
import {AnnouncementDto} from '@lib/infrastructure';
import {createSelector} from 'reselect';

export const selectEmployeeCommunicationAnnouncements = (state: RootState) =>
  state.data.employeeCommunication.announcements;
export const selectEmployeeCommunicationLoadingStatus = (state: RootState) =>
  state.data.employeeCommunication.loadingStatus;

export const selectAllMeetingSuggestionsWithOrganizerAttendees = createSelector(
  getAllWorkdays,
  // Good luck figuring out why the test brakes
  // when using `getAllWorkspaceReservations` the test fails with
  // `createSelector expects all input-selectors to be functions, but received the following types: [function wrappedSelector(), undefined, function unnamed()]`
  (state: RootState) => Object.values(state.data.workspaceReservations.workspaceReservations.entities),
  (state: RootState) => state.data.employeeCommunication.announcements,
  (workdays, workspaceReservations, announcements) => {
    const filteredAnnouncements: AnnouncementDto[] = [];

    for (const announcement of announcements) {
      const {targetType} = announcement.targetFilter;

      if (targetType === 'DefaultBuilding') {
        continue;
      }

      if (targetType === 'All') {
        filteredAnnouncements.push(announcement);
        continue;
      }

      if (targetType === 'WorkingLocation') {
        const {buildingId, floorId, areaId} = announcement.targetFilter;

        if (buildingId && !floorId) {
          const hasMatchingWorkdays = workdays.some((workday) => {
            if (workday.status !== 'OfficeDay' || buildingId !== workday.nodeId) return false;

            const workdayDate = parseISO(workday.startDate);
            const isWorkdayWithinInterval = isWithinInterval(workdayDate.setHours(0, 0, 0, 0), {
              start: new Date(announcement.startDateTimeLocal).setHours(0, 0, 0, 0),
              end: new Date(announcement.endDateTimeLocal).setHours(0, 0, 0, 0),
            });

            if (!isWorkdayWithinInterval) return false;

            return true;
          });

          if (hasMatchingWorkdays) {
            filteredAnnouncements.push(announcement);
            continue;
          }
        } else {
          const hasMatchingWorkspaceReservations = workspaceReservations.some((workspaceReservation) => {
            const {location} = workspaceReservation.currentMember || {};
            if (!location) return false;

            if (buildingId !== location.buildingId) return false;
            if (floorId && floorId !== location?.floorId) return false;
            if (areaId && areaId !== location?.areaId) return false;

            const workspaceReservationDate = parseISO(workspaceReservation.startDate);
            const isReservationWithinInterval = isWithinInterval(workspaceReservationDate.setHours(0, 0, 0, 0), {
              start: new Date(announcement.startDateTimeLocal).setHours(0, 0, 0, 0),
              end: new Date(announcement.endDateTimeLocal).setHours(0, 0, 0, 0),
            });

            if (!isReservationWithinInterval) return false;

            return true;
          });

          if (hasMatchingWorkspaceReservations) {
            filteredAnnouncements.push(announcement);
            continue;
          }
        }

        continue;
      }
    }

    return filteredAnnouncements;
  },
);
